import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart2, CheckCircle, XCircle, Clock, Send, RefreshCw,
  MessageCircle, Zap, AlertCircle, TrendingUp, Eye, Trash2,
  ChevronRight, Activity, Linkedin,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { toast } from '../components/ui/sonner';
import SnapshotBadge from '../components/SnapshotBadge';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  draft: { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', label: 'Draft' },
  pending_review: { color: '#f0c27f', bg: 'rgba(240,194,127,0.1)', label: 'Pending' },
  approved: { color: '#3dffa0', bg: 'rgba(61,255,160,0.1)', label: 'Approved' },
  published: { color: '#a68eff', bg: 'rgba(166,142,255,0.1)', label: 'Published' },
  declined: { color: '#ff5370', bg: 'rgba(255,83,112,0.1)', label: 'Declined' },
};

const ACTION_META = {
  approve: { icon: CheckCircle, color: '#3dffa0', label: 'Approved' },
  decline: { icon: XCircle, color: '#ff5370', label: 'Declined' },
  approved: { icon: CheckCircle, color: '#3dffa0', label: 'Approved' },
  declined: { icon: XCircle, color: '#ff5370', label: 'Declined' },
  feedback: { icon: MessageCircle, color: '#60a5fa', label: 'Feedback' },
  rewrite: { icon: RefreshCw, color: '#f0c27f', label: 'Rewrite' },
  notification_sent: { icon: Send, color: '#7289da', label: 'Sent to Discord' },
  published: { icon: Zap, color: '#a68eff', label: 'Published' },
};

function StatCard({ label, value, color, icon: Icon, sub }) {
  return (
    <div className="rounded-2xl p-4 flex items-center gap-3.5"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--llmc-border)' }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-2xl font-black text-white">{value}</div>
        <div className="text-xs font-medium truncate" style={{ color: 'var(--llmc-text-muted)' }}>{label}</div>
        {sub && <div className="text-[10px]" style={{ color: `${color}80` }}>{sub}</div>}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl px-3 py-2 text-xs"
      style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
      <div className="font-bold mb-1">{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</div>
      ))}
    </div>
  );
}

function ActivityRow({ item }) {
  const action = item.action || item.event_type || 'notification_sent';
  const meta = ACTION_META[action] || { icon: Activity, color: '#aaa', label: action };
  const Icon = meta.icon;
  const ts = item.timestamp ? new Date(item.timestamp).toLocaleString() : '';

  return (
    <div className="flex items-center gap-3 py-2.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${meta.color}12` }}>
        <Icon size={12} style={{ color: meta.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-white truncate">
          <span style={{ color: meta.color }}>{meta.label}</span>
          {item.content_id && <span className="ml-1.5 font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>#{item.content_id?.slice(-6)}</span>}
        </div>
        <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>
          by <span style={{ color: 'rgba(255,255,255,0.5)' }}>{item.user || 'Discord'}</span>
          {item.note && <span className="ml-1.5">{item.note}</span>}
        </div>
      </div>
      <div className="text-[9px] flex-shrink-0" style={{ color: 'rgba(255,255,255,0.25)' }}>{ts}</div>
    </div>
  );
}

export default function ReviewDashboard() {
  const [queue, setQueue] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notifying, setNotifying] = useState({});
  const [publishing, setPublishing] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, aRes] = await Promise.all([
        axios.get(`${API}/api/social/queue/all`).catch(() => ({ data: { posts: [] } })),
        axios.get(`${API}/api/discord/activity`).catch(() => ({ data: { activity: [] } })),
      ]);
      setQueue(qRes.data?.posts || qRes.data?.items || qRes.data?.queue || []);
      setActivity(aRes.data?.activity || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendToDiscord = async (item) => {
    setNotifying(n => ({ ...n, [item.id]: true }));
    try {
      await axios.post(`${API}/api/discord/notify`, {
        content_id: item.id,
        title: item.title || item.topic || 'Untitled',
        preview: item.preview || item.content?.slice(0, 500) || '',
        platform: item.platform || 'blog',
        brand: item.brand || 'qlia.io',
      });
    } catch (e) {
      console.warn('Discord notify failed:', e?.response?.data?.detail);
    }
    setNotifying(n => ({ ...n, [item.id]: false }));
    load();
  };

  const approveContent = async (contentId) => {
    try {
      await axios.put(`${API}/api/social/queue/${contentId}/status`, { status: 'approved' });
      load();
    } catch (e) {
      console.warn('Approve failed:', e?.response?.data?.detail);
    }
  };

  const approveAndPublish = async (item) => {
    setPublishing(p => ({ ...p, [item.id]: true }));
    try {
      // Step 1: Approve in queue
      await axios.put(`${API}/api/social/queue/${item.id}/status`, { status: 'approved' });
      // Step 2: Publish to LinkedIn
      await axios.post(`${API}/api/linkedin/publish`, {
        content: item.content || item.preview || '',
        title: item.title || item.topic || 'Untitled',
        brand: item.brand || 'qlia.io',
      });
      toast?.success?.('Published to LinkedIn!');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Publish failed';
      toast?.error?.(detail);
      console.warn('Approve+Publish failed:', detail);
      // Still reload to reflect approved state
      load();
    } finally {
      setPublishing(p => ({ ...p, [item.id]: false }));
    }
  };

  const declineContent = async (contentId) => {
    try {
      await axios.put(`${API}/api/social/queue/${contentId}/status`, { status: 'declined' });
      load();
    } catch (e) {
      console.warn('Decline failed:', e?.response?.data?.detail);
    }
  };

  // Stats
  const total = queue.length;
  const statusCounts = queue.reduce((acc, item) => {
    const s = item.status || 'draft';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const approved = statusCounts.approved || 0;
  const declined = statusCounts.declined || 0;
  const pending = (statusCounts.pending_review || 0) + (statusCounts.pending || 0);
  const published = statusCounts.published || 0;
  const draft = statusCounts.draft || 0;
  const totalDecided = approved + declined;
  const approvalRate = totalDecided > 0 ? Math.round((approved / totalDecided) * 100) : 0;

  // Chart data
  const pipelineData = [
    { name: 'Draft', value: draft, color: '#60a5fa' },
    { name: 'Pending', value: pending, color: '#f0c27f' },
    { name: 'Approved', value: approved, color: '#3dffa0' },
    { name: 'Published', value: published, color: '#a68eff' },
    { name: 'Declined', value: declined, color: '#ff5370' },
  ].filter(d => d.value > 0);

  const activityByDay = (() => {
    const byDay = {};
    activity.forEach(a => {
      const day = a.timestamp ? new Date(a.timestamp).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'Unknown';
      if (!byDay[day]) byDay[day] = { day, approve: 0, decline: 0, feedback: 0 };
      if (a.action === 'approve') byDay[day].approve++;
      else if (a.action === 'decline') byDay[day].decline++;
      else if (a.action === 'feedback') byDay[day].feedback++;
    });
    return Object.values(byDay).slice(-7);
  })();

  const filteredQueue = activeFilter === 'all' ? queue : queue.filter(i => (i.status || 'draft') === activeFilter);

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4" style={{ borderBottom: '1px solid var(--llmc-border)', background: 'rgba(10,10,20,0.95)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #7289da, #5865f2)' }}>
              <BarChart2 size={20} className="text-white" />
            </div>
            <div>
              <div className="font-black text-white text-sm">Content Review Dashboard</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                Pipeline health · Discord approval workflow · Content queue
              </div>
            </div>
          </div>
          <button onClick={load} disabled={loading}
            data-testid="dashboard-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-40"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}>
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* Stat Cards */}
        <div className="grid grid-cols-5 gap-3" data-testid="dashboard-stats">
          <StatCard label="Total Content" value={total} color="#7289da" icon={Eye} />
          <StatCard label="Pending Review" value={pending} color="#f0c27f" icon={Clock} sub={pending > 0 ? "Needs action" : "Queue clear!"} />
          <StatCard label="Approved" value={approved} color="#3dffa0" icon={CheckCircle} />
          <StatCard label="Published" value={published} color="#a68eff" icon={Send} />
          <StatCard label="Approval Rate" value={`${approvalRate}%`} color={approvalRate >= 70 ? '#3dffa0' : '#f0c27f'} icon={TrendingUp} sub={`${totalDecided} decisions made`} />
        </div>

        <div className="grid grid-cols-3 gap-5">
          {/* Pipeline Donut */}
          <div className="rounded-2xl p-4 space-y-3"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
            <div className="text-xs font-bold text-white">Content Pipeline</div>
            {pipelineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pipelineData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                    {pipelineData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend formatter={(value) => <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-40 flex items-center justify-center text-xs" style={{ color: 'var(--llmc-text-muted)' }}>No content in pipeline</div>
            )}
          </div>

          {/* Activity Bar Chart */}
          <div className="rounded-2xl p-4 space-y-3"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
            <div className="text-xs font-bold text-white">Discord Activity (Last 7 Days)</div>
            {activityByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={activityByDay} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="approve" name="Approved" fill="#3dffa0" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="decline" name="Declined" fill="#ff5370" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="feedback" name="Feedback" fill="#60a5fa" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-40 flex flex-col items-center justify-center gap-2">
                <MessageCircle size={24} style={{ color: 'rgba(255,255,255,0.15)' }} />
                <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>No Discord activity yet</div>
                <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.2)' }}>Use "Send to Discord" on pending content</div>
              </div>
            )}
          </div>

          {/* Recent Activity Feed */}
          <div className="rounded-2xl p-4 space-y-2 flex flex-col"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-white">Recent Activity</div>
              <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>{activity.length} events</div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {activity.length === 0 ? (
                <div className="text-xs py-4 text-center" style={{ color: 'var(--llmc-text-muted)' }}>
                  No interactions yet
                </div>
              ) : (
                activity.slice(0, 12).map((item, i) => <ActivityRow key={i} item={item} />)
              )}
            </div>
          </div>
        </div>

        {/* Content Queue Table */}
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--llmc-border)' }}>
          <div className="flex items-center justify-between px-5 py-3"
            style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--llmc-border)' }}>
            <div className="text-xs font-bold text-white">Content Queue</div>
            {/* Filter tabs */}
            <div className="flex gap-1">
              {['all', 'pending_review', 'approved', 'declined'].map(f => (
                <button key={f} onClick={() => setActiveFilter(f)}
                  data-testid={`filter-${f}`}
                  className="px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all"
                  style={{
                    background: activeFilter === f ? 'rgba(114,137,218,0.2)' : 'transparent',
                    color: activeFilter === f ? '#7289da' : 'rgba(255,255,255,0.4)',
                  }}>
                  {f === 'all' ? `All (${total})` : STATUS_META[f]?.label || f}
                </button>
              ))}
            </div>
          </div>

          {filteredQueue.length === 0 ? (
            <div className="text-center py-8 text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
              No content in this category
            </div>
          ) : (
            <div className="divide-y" style={{ '--tw-divide-opacity': 0.05, borderColor: 'rgba(255,255,255,0.05)' }}>
              {filteredQueue.slice(0, 20).map((item) => {
                const statusMeta = STATUS_META[item.status || 'draft'] || STATUS_META.draft;
                return (
                  <div key={item.id} className="flex flex-col px-5 py-3.5 hover:bg-white/[0.02] transition-colors"
                    data-testid="queue-item">
                    <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white truncate">{item.title || item.topic || 'Untitled'}</div>
                      <div className="text-[10px] truncate" style={{ color: 'var(--llmc-text-muted)' }}>
                        {item.platform || 'blog'} · {item.brand || 'qlia.io'}
                        {item.created_at && ` · ${new Date(item.created_at).toLocaleDateString()}`}
                        {item.version > 1 && <span style={{ color: '#a68eff' }}> · v{item.version}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background: statusMeta.bg, color: statusMeta.color }}>
                        {statusMeta.label}
                      </span>
                      {(item.status === 'pending_review' || item.status === 'draft') && (
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => sendToDiscord(item)}
                            disabled={notifying[item.id]}
                            data-testid={`send-discord-${item.id}`}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all hover:opacity-80 disabled:opacity-40"
                            style={{ background: 'rgba(114,137,218,0.12)', border: '1px solid rgba(114,137,218,0.3)', color: '#7289da' }}>
                            {notifying[item.id] ? <RefreshCw size={9} className="animate-spin" /> : <MessageCircle size={9} />}
                            {notifying[item.id] ? 'Sending...' : 'Discord'}
                          </button>
                          <button
                            onClick={() => approveContent(item.id)}
                            data-testid={`approve-${item.id}`}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all hover:opacity-80"
                            style={{ background: 'rgba(61,255,160,0.12)', border: '1px solid rgba(61,255,160,0.3)', color: '#3dffa0' }}>
                            <CheckCircle size={9} /> Approve
                          </button>
                          <button
                            onClick={() => approveAndPublish(item)}
                            disabled={publishing[item.id]}
                            data-testid={`approve-publish-${item.id}`}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all hover:opacity-80 disabled:opacity-40"
                            style={{ background: 'rgba(10,102,194,0.15)', border: '1px solid rgba(10,102,194,0.35)', color: '#4f9cf9' }}>
                            {publishing[item.id] ? <RefreshCw size={9} className="animate-spin" /> : <Linkedin size={9} />}
                            {publishing[item.id] ? '...' : '+ LinkedIn'}
                          </button>
                          <button
                            onClick={() => declineContent(item.id)}
                            data-testid={`decline-${item.id}`}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all hover:opacity-80"
                            style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171' }}>
                            <XCircle size={9} /> Decline
                          </button>
                        </div>
                      )}
                    </div>
                    </div>
                    {/* Pipeline snapshot */}
                    {item.snapshot && (
                      <div className="mt-2 ml-0">
                        <SnapshotBadge snapshot={item.snapshot} type="article" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
